<template>
  <wa-page
    :is-showing-bandana="false"
    :is-showing-sitewide-modal="false"
    :is-showing-v7-prerelease-notice="true"
  >
    <wa-page-header
      slot="header"
      key="header"
    />

    <main
      slot="content"
      role="main"
      class="app-content"
    >
      <plans-header-section
        :is-affiliate-referral="isAffiliateReferral"
        :affiliate="affiliate"
        :has-active-old-plan="hasActiveOldPlan"
        :has-not-offered-plan="hasNotOfferedPlan"
        :current-sub-name="currentSubName"
      />

      <template v-if="saleActive && !isMaxUser && !hasPlusPlan">
        <promo-alert
          :promo-code="promoCode"
          :discount="discountPercent"
        />
      </template>

      <section
        v-if="isUpgrading && !saleActive"
        class="size-md tablet:size-sm margin-top-4xl"
      >
        <div class="container">
          <div class="row roomy">
            <div class="column-12 laptop:column-10 laptop:offset-2">
              <p class="message success size-reset-md margin-bottom-0">
                <strong>Hey Awesome Subscriber!</strong> Since you already have a subscription, the cost of any plan upgrade will be pro-rated.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        :class="`padding-bottom-xl size-md tablet:size-sm margin-top-4xl`"
        style="--sale-theme-color: var(--fa-teal); --sale-theme-color-dk: var(--fa-dk-teal); --sale-link-color: var(--fa-navy)"
      >
        <div class="container">
          <div
            v-if="plusPlansEnabled"
            class="row roomy margin-bottom-3xl"
          >
            <div
              class="column-12 laptop:column-10 laptop:offset-2"
              style="display: flex; place-content: center"
            >
              <slide-toggle
                v-model="viewablePlans"
                @toggle-plans="togglePlans"
              />
            </div>
          </div>

          <wa-icons-loading
            v-if="isLoading"
            :transition="false"
          />

          <div
            v-else
            class="row roomy align-bottom"
          >
            <plans-supplementary-info-section />

            <div class="plans-summary-cards column-12 laptop:column-10 tablet:display-flex">
              <plan-summary-card
                v-for="sku in plans"
                :key="sku"
                :plan="planBySku(sku)"
                :plan-color="planColor(sku)"
                :plan-icon-style="planIconStyle(sku)"
                :current-subscription="currentSubscription"
                :subscription-discounts="subscriptionDiscounts"
                :on-sale="onSale(sku)"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="margin-bottom-6xl size-md tablet:size-sm">
        <div
          class="container"
          style="position: relative"
        >
          <div
            v-if="plusPlansEnabled"
            class="column-12 laptop:column-10 laptop:offset-2 margin-top-xl laptop:margin-top-0 tablet:display-flex align-between laptop:align-center laptop:padding-x-sm"
          >
            <h3 class="laptop:display-none">Compare Plans</h3>
            <div>
              <input
                id="compare-all"
                v-model="compareAllPlans"
                type="checkbox"
                class="with-icon"
              />
              <label
                for="compare-all"
                style="cursor: pointer; display: flex !important; gap: var(--spacing-xs)"
              >
                <span class="position-relative">
                  <font-awesome-icon
                    :icon="$v6.solid.faSquare"
                    fixed-width
                    size="lg"
                    class="unchecked-icon"
                    style="color: var(--white)"
                  />
                  <font-awesome-icon
                    :icon="$v6.solid.faSquare"
                    fixed-width
                    size="lg"
                    class="hover-icon"
                    style="color: var(--white)"
                  />
                  <font-awesome-icon
                    :icon="$v6.solid.faSquareCheck"
                    fixed-width
                    size="lg"
                    class="checked-icon"
                  />
                </span>
                <span class="label display-block margin-top-0 margin-bottom-3xs">Compare All Plans</span>
              </label>
            </div>
          </div>
          <div :class="['wrap-compare-tables', { 'comparing-all': compareAllPlans }]">
            <div
              v-for="(tableRow, index) in tableRows"
              :key="index"
              class="plan-compare-table padding-y-xs"
              style="background-color: var(--fa-lt-gravy)"
            >
              <plan-compare-table
                v-for="(row, tIndex) in tableRow"
                :key="tIndex"
                v-bind="row"
                :plans-by-sku="comparablePlans"
                :current-plans="currentPlans"
              />
            </div>
          </div>
        </div>
      </section>

      <faqs-section
        :is-affiliate-referral="isAffiliateReferral"
        :affiliate="affiliate"
      />
    </main>
    <wa-page-footer slot="footer" />
  </wa-page>
</template>

<script>
  import { get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import {
    ALL_PLANS_BY_SKU,
    AUTO_APPLIED_DISCOUNT_CODES,
    AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES,
    PROMO_CODES,
    shouldHavePromoCode,
    getDiscountPercent,
    NON_OFFERED_PLANS,
    MAX_PLANS_BY_SKU,
    PLANS_BY_SKU,
    PLAN_COLOR_BY_SKU,
    PLAN_ICONSTYLE_BY_SKU,
    TABLE_ROWS,
    PLUS_PLANS_BY_SKU,
    v7PreSaleStatus,
    PLANS_ON_SALE_BY_SKU,
    PLANS_ON_SALE_FOR_ALL_BY_SKU
  } from './PlansData.js'
  import FaqsSection from './sections/FaqsSection'
  import PromoAlert from './sections/PromoAlert'
  import PlanCompareTable from './components/PlanCompareTable'
  import PlansHeaderSection from './sections/PlansHeaderSection'
  import PlansSupplementaryInfoSection from './sections/PlansSupplementaryInfoSection'
  import PlanSummaryCard from './components/PlanSummaryCard'
  import SlideToggle from './components/SlideToggle'

  export default {
    name: 'PlansView',

    components: {
      FaqsSection,
      PromoAlert,
      PlanCompareTable,
      PlanSummaryCard,
      PlansSupplementaryInfoSection,
      PlansHeaderSection,
      SlideToggle
    },

    data() {
      return {
        viewablePlans: this.$route.query.plans ? this.$route.query.plans : 'plus',
        compareAllPlans: false,
        plusPlansEnabled: v7PreSaleStatus.saleActive,
        saleActive: v7PreSaleStatus.saleActive,
        tableRows: TABLE_ROWS,
        isLoading: true,
        promoCode: null
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),
      ...mapGetters('plans', ['currentPlans', 'getPlan', 'affiliateMeta', 'subscriptionDiscounts']),
      ...mapGetters('subscriptions', ['currentSubscription']),
      ...mapGetters('discounts', ['globalDiscountCode']),

      isAffiliateReferral() {
        return !!get(this, ['affiliateMeta', 'token'])
      },

      affiliate() {
        return this.affiliateMeta
      },

      currentSubName() {
        return get(this.currentSubscription, ['plan', 'name'], null)
      },

      hasNotOfferedPlan() {
        const currentSubSku = get(this.currentSubscription, ['plan', 'sku'])

        return NON_OFFERED_PLANS.includes(currentSubSku)
      },

      hasActiveOldPlan() {
        const currentSubStatus = get(this.currentSubscription, ['status'])
        const isInactivePlan = get(this.currentSubscription, ['plan', 'active'])

        return currentSubStatus === 'active' && !isInactivePlan
      },

      isUpgrading() {
        if (this.currentSubscription) {
          return this.currentSubscription.status === 'active' && this.currentSubscription.total.amount !== 0
        } else {
          return false
        }
      },

      isShowingDiscount() {
        return this.subscriptionDiscounts.length > 0
      },

      isMaxUser() {
        return MAX_PLANS_BY_SKU.includes(get(this.currentSubscription, ['plan', 'sku'], ''))
      },

      isProBacker() {
        if (!this.currentAccount) {
          return false
        }

        return this.currentAccount.isBacker && this.currentSubscription
      },

      isBackerWithoutActiveSubscription() {
        if (!this.currentAccount) {
          return false
        }

        return this.currentAccount.isBacker && get(this.currentSubscription, ['status'], null) !== 'active'
      },

      discountPercent() {
        return getDiscountPercent(this.currentAccount, this.currentSubscription)
      },

      plans() {
        if (v7PreSaleStatus.saleActive) {
          return this.viewablePlans === 'pro' ? PLANS_BY_SKU : PLUS_PLANS_BY_SKU
        }
        
        return PLANS_BY_SKU
      },

      comparablePlans() {
        if (this.compareAllPlans) {
          return ALL_PLANS_BY_SKU
        } else {
          return this.plans
        }
      },

      hasActivePaidPlan() {
        return get(this.currentSubscription, ['total', 'amount'], 0) > 0 && get(this.currentSubscription, ['status'], null) === 'active'
      },

      hasPlusPlan() {
        return PLUS_PLANS_BY_SKU.includes(get(this.currentSubscription, ['plan', 'sku'], null))
      }
    },

    async created() {
      const data = [this.fetchPlans()]

      if (v7PreSaleStatus.saleActive) {
        if (shouldHavePromoCode(this.currentAccount, this.currentSubscription)) {
          data.push(this.fetchDiscounts([PROMO_CODES[this.discountPercent]['id']]))
          this.promoCode = PROMO_CODES[this.discountPercent]['code']
        } else if (this.globalDiscountCode) {
          data.push(this.fetchDiscounts([this.globalDiscountCode]))
        } else if (AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES.length > 0 && this.isBackerWithoutActiveSubscription) {
          data.push(this.fetchDiscounts(AUTO_APPLIED_KICKSTARTER_DISCOUNT_CODES))
        } else if (AUTO_APPLIED_DISCOUNT_CODES.length > 0) {
          data.push(this.fetchDiscounts(AUTO_APPLIED_DISCOUNT_CODES))
        }
      }

      try {
        await Promise.all(data)
      } finally {
        this.isLoading = false
      }
    },

    mounted() {
      this.$posthog.capture('plans_view', this.determinationsEventProps)
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts']),

      planBySku(sku) {
        if (this.currentPlans.length > 0) {
          return this.currentPlans.find((plan) => plan.sku == sku)
        } else {
          return {}
        }
      },

      onSale(sku) {
        if (v7PreSaleStatus.saleActive && !this.hasActivePaidPlan && !this.hasPlusPlan) {
          return PLANS_ON_SALE_BY_SKU.includes(sku)
        } else if (v7PreSaleStatus.saleActive && !this.hasPlusPlan) {
          return PLANS_ON_SALE_FOR_ALL_BY_SKU.includes(sku)
        }

        return false
      },

      planColor(sku) {
        return get(PLAN_COLOR_BY_SKU, [sku], 'yellow')
      },

      planIconStyle(sku) {
        return get(PLAN_ICONSTYLE_BY_SKU, [sku], 'solid')
      },

      togglePlans(plans) {
        this.viewablePlans = plans
      }
    }
  }
</script>
